"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function OurSacredMission() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="86492" id="the-moonrise-difference" className="bg-background py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="font-sans text-5xl md:text-6xl lg:text-7xl text-primary font-bold leading-tight mb-10 tracking-tighter">
              A true Home Setting
            </h2>
            
            <div className="space-y-8 text-muted-foreground text-lg md:text-xl leading-relaxed font-sans font-normal">
              <p>
                At Moonrise Residential Care, we believe that the environment is just as important as the care provided. Our residence in McKinney, Texas, is designed to feel like a true home, not an institution. We offer a sanctuary where seniors can enjoy their golden years surrounded by warmth, comfort, and a dedicated team who treat every resident like family.
              </p>
              <p>
                Our commitment to high-quality elder care is rooted in the understanding that every individual has unique needs and stories. From personalized daily living assistance to medication management, our staff ensures that your loved ones receive attentive care in a peaceful, secure setting. We prioritize dignity, safety, and a deep sense of community in everything we do.
              </p>
              <p>
                Founded on the principles of compassion and excellence, Moonrise provides a loving alternative to traditional senior living. Here, residents find more than just a place to stay—they find a home where they can flourish, engage in social activities, and experience the peace of mind that comes from being truly cared for in a professional yet intimate environment.
              </p>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative w-full max-w-md mx-auto lg:ml-auto lg:mr-8">
              {}
              <div className="absolute inset-0 border-[3px] border-[#ffbd00] translate-x-5 translate-y-5 rounded-sm transition-transform duration-700 hover:translate-x-6 hover:translate-y-6" aria-hidden="true" />
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1616978/img/church_interior_sanctuary.png" alt="Cozy living room area in a senior residential care home" className="relative z-10 w-full aspect-[3/4] object-cover rounded-sm shadow-xl" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}